import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Space } from 'antd';
import { useCallback, useRef, useState } from 'react';
import AgencyForm, {
  AgencyFormRef,
  AgencyFormValues,
} from '../components/AgencyForm';
import { useCreateAgency } from '@nxsys/hooks/api/agency';
import { CreateAgencyPayload } from '@nxsys/types/api';
import { showMessage } from '@nxsys/utils/message';

const CreateAgency = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const [isCreatingAgency, setIsCreatingAgency] = useState<boolean>(false);

  const formRef = useRef<AgencyFormRef>(null);
  const { run: createAgency } = useCreateAgency();

  const handleGoBack = () => navigate('/agency');

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-x-3">
          <AppPageTitle
            separator=""
            prefix={messages['prospect.addNewAgency']}
            title=""
          />
        </div>
      </div>
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center justify-between">
        <Space>
          <AppButton
            variant="outlined"
            type="primary"
            name={messages['common.cancel'] || 'Cancel'}
            onClick={handleGoBack}
          />
          <AppButton
            type="primary"
            name={messages['common.save'] || 'Save'}
            onClick={() => {
              formRef.current?.submit();
            }}
          />
        </Space>
      </div>
    );
  }, [messages, formRef]);

  const handleSubmit = (values: AgencyFormValues) => {
    setIsCreatingAgency(true);

    const params: CreateAgencyPayload = {
      name: values.agencyName ?? '',
      agencyPrefix: values.agencyPrefix ?? '',
      currency: values.currency?.value ?? '',
      paymentModel: values.paymentModel?.map((item) => item.value) ?? [],
      allowPortalAccess: values.allowPortalAccess ?? false,
      timesheetApprovalNeeded: values.timesheetApprovalNeeded || '',
      timesheetApproverIds: [],
      enableBenefits: values.enableBenefits ?? false,
      emergencyContact: {
        contactName: values.emergencyContactName || '',
        email: values.emergencyEmail || null,
        telephone: values.emergencyTelephone || null,
        mobile: values.emergencyMobile || null,
      },
      contactDetail: {
        contactName: values.contactDetails?.name || '',
        address: [
          values.contactDetails?.address1 || '',
          values.contactDetails?.address2 || '',
          values.contactDetails?.address3 || '',
        ].filter(Boolean),
        city: values.contactDetails?.city || '',
        county: values.contactDetails?.county || '',
        country: values.contactDetails?.country?.value || '',
        postCode: values.contactDetails?.postCode || '',
        email: values.contactDetails?.email || null,
        mobile: values.contactDetails?.mobile || null,
        onboardingEmailNotification:
          values.contactDetails?.onboardingEmailNotification || [],
      },
      branches: [],
      payrollFinance: {
        invoicing: {
          invoicingMethod: undefined,
          paymentTerm: undefined,
          domesticReverseChargeApplicable: undefined,
          vatRate: undefined,
        },
        margin: {
          marginMethod: undefined,
          margin: '',
          marginCapped: '',
          minMargin: '',
        },
        bank: {
          bankName: '',
          accountName: '',
          accountNumber: '',
          sortCode: '',
          buildingSocietyNumber: undefined,
          IBANReference: undefined,
          SWIFTNo: undefined,
        },
        holidayPay: {
          holidayPayMethod: undefined,
          umbrellaHolidayPayMethod: undefined,
          payeHolidayPayRate: undefined,
          umbrellaHolidayPayRate: undefined,
        },
      },
    };

    createAgency(params)
      .then(() => {
        showMessage({
          type: 'success',
          content: messages['prospect.agency.createSuccess'],
        });
        handleGoBack();
      })
      .finally(() => {
        setIsCreatingAgency(false);
      });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isCreatingAgency}
    >
      <AgencyForm formRef={formRef} onSubmit={handleSubmit} />
    </AppsContainer>
  );
};

export default CreateAgency;
